import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  BusinessDayCloseController_reopenDay_v1,
  type BusinessDayCloseController_reopenDay_v1Body,
  type DayReopenResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { formatBusinessDate } from '../tickets/business-date';
import { reopenDaySchema, type ReopenDayValues } from './day-close-form';

/**
 * Putting a closed day back:
 * `POST /v1/outlets/{outletId}/business-days/{businessDate}/reopen`.
 *
 * ## An owner's act, and the screen must not suggest otherwise
 *
 * The route admits `OWNER` and nobody else. A manager may close any outlet's day
 * and may not reopen one, *whatever outlet they are assigned to*, because
 * reopening undoes a declaration another person signed off (CL-603). The two
 * acts on this screen therefore do not carry the same guard, and the screen
 * shows this control to an owner only.
 *
 * **That asymmetry has already cost this repo once**: two refusal messages told
 * people to fetch a manager for an act only an owner may perform, corrected in
 * 141. Nothing in this file or in the copy around it may reintroduce it — where
 * reopening is named, the word is *owner*.
 *
 * Hiding the control is courtesy and not authorisation. `role` comes from
 * `sessionStorage` and is editable by whoever is at the keyboard, so a manager
 * who puts the button back still meets the server's refusal, and the refusal is
 * worded by `error-messages.ts` like every other.
 *
 * ## Nothing is erased, which is why the reason is required
 *
 * A reopen **appends** (FR-613c). The earlier close keeps its counted figure and
 * its actor, `reopenCount` goes up, and the day's history carries every act in
 * order. So this dialog is not undoing a record — it is adding one that says why
 * the earlier one was set aside, and a reopen with no reason is indistinguishable
 * from a mistake being covered up (FR-613b). There is deliberately no counted
 * figure here: a reopen does not recount the drawer, the next close does
 * (FR-619c).
 *
 * Not styled `destructive`, and that is a decision rather than an oversight.
 * `docs/ui-conventions.md` reserves red for irreversible acts and for refusals;
 * this is neither. It appends, it is itself undone by closing again, and
 * colouring it as damage would misdescribe the one act in the system that exists
 * to let somebody put a day right.
 */
export function ReopenDayDialog({
  open,
  onOpenChange,
  outletId,
  outletName,
  businessDate,
  onReopened,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outletId: string;
  outletName: string;
  businessDate: string;
  onReopened: (reopened: DayReopenResource) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Reopen {formatBusinessDate(businessDate)}</DialogTitle>
          <DialogDescription>
            {outletName}. The earlier close stays in the day&rsquo;s history.
          </DialogDescription>
        </DialogHeader>

        {/* A child, so closing the dialog unmounts it and the next reopen starts
            from an empty field rather than from somebody else's reason. */}
        <ReopenDayForm outletId={outletId} businessDate={businessDate} onReopened={onReopened} />
      </FormDialogContent>
    </Dialog>
  );
}

function ReopenDayForm({
  outletId,
  businessDate,
  onReopened,
}: {
  outletId: string;
  businessDate: string;
  onReopened: (reopened: DayReopenResource) => void;
}) {
  const client = useApiClient();

  const form = useForm<ReopenDayValues>({
    resolver: zodResolver(reopenDaySchema),
    mode: 'onTouched',
    defaultValues: { reason: '' },
  });

  const [formError, setFormError] = useState<string | null>(null);

  /** The attempt this dialog is currently under. Nothing renders from it. */
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async (values: ReopenDayValues) => {
    if (pending) return;

    setFormError(null);

    // The reason belongs to the fingerprint: somebody refused, correcting what
    // they wrote and pressing again is a different body, and sending it under
    // the held key would earn `409 SYNC_IDEMPOTENCY_KEY_REUSED` — a refusal no
    // retry can clear. `ReopenBusinessDayDto` carries no client-minted `id`, so
    // only the attempt's key is read; see `CloseDayDialog` for why that is worth
    // the unused UUID.
    const sending = attemptFor(
      attempt.current,
      JSON.stringify([outletId, businessDate, values.reason]),
    );

    attempt.current = sending;

    try {
      // Sent exactly as typed. The API stores a reason "exactly as submitted"
      // and the event cannot afterwards be edited, so a client that quietly
      // trimmed it would be editing something nobody can put right. The schema
      // has already refused one that is blank once trimmed.
      const body: BusinessDayCloseController_reopenDay_v1Body = { reason: values.reason };

      onReopened(
        await client.request(BusinessDayCloseController_reopenDay_v1, {
          path: { outletId, businessDate },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error: unknown) {
      if (error instanceof ApiError && error.code === 'DAY_CLOSE_REASON_INVALID') {
        form.setError('reason', { message: messageForFailure(error) });
        form.setFocus('reason');

        return;
      }

      // `DAY_CLOSE_NOT_CLOSED` lands here, and its copy already says the likely
      // cause: somebody else reopened it and this screen is behind. So does
      // `AUTH_FORBIDDEN`, which is what a manager who edited their stored role
      // meets — the server refusing what this screen declined to offer.
      setFormError(messageForFailure(error));
    }
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Why it is being reopened
              </FormLabel>
              <FormControl
                render={
                  <Input
                    // An `Input` and not a `textarea`: the registry has not
                    // published one into `packages/ui`, and every other reason
                    // field in this app — a void, a refund, an expense — is
                    // single-line for the same reason. Adding one is a
                    // `shadcn add`, which lists `button` among its registry
                    // dependencies and has eaten the button's `touch` sizes
                    // three times. Noted in the PR body as the control this
                    // field actually wants.
                    className={`${fieldSurface} h-11 w-full`}
                    autoComplete="off"
                    placeholder="Two sales were still queued on till 3 when the day was closed."
                    required
                    {...field}
                  />
                }
              />
              <FormDescription>Cannot be edited afterwards.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending}>
            {pending ? 'Reopening…' : 'Reopen the day'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
